import {h, Fragment} from "preact"
import { useEffect } from "preact/hooks"
import { useResponse, AutocompleteElement, Keywords, useCurrencyFormatting, useAppStateSelector, pick } from "@nosto/preact"
import { productImagePlaceholder, trendingSearches, trendingCollections, updateSearchAria } from "../helpers"
import SubmitButton from "./elements/SubmitButton"

export default function Autocomplete() {
    const { products, keywords } = useResponse()
    const { format } = useCurrencyFormatting()

    if (!products?.hits?.length && !keywords?.hits?.length) {
        return
    }

    const { query } = useAppStateSelector(state =>
                pick(state, "query")
    )

    // Update Aria and keyboard navigation
    useEffect(() => {
        updateSearchAria('autocomplete');
        // Announce number of results to screen readers
        const resultsCount = (products?.hits?.length || 0) + (keywords?.hits?.length || 0);
        const announcement = `Autocomplete results updated. ${resultsCount} suggestions available. Use arrow keys to navigate.`;
        
        // Create or update live region for announcements
        let liveRegion = document.getElementById('autocomplete-live-region');
        if (!liveRegion) {
            liveRegion = document.createElement('div');
            liveRegion.id = 'autocomplete-live-region';
            liveRegion.className = 'sr-only';
            liveRegion.setAttribute('aria-live', 'polite');
            liveRegion.setAttribute('aria-atomic', 'true');
            document.body.appendChild(liveRegion);
        }
        liveRegion.textContent = announcement;

        // Add keyboard navigation for autocomplete
        const handleKeyDown = (e) => {
            const searchInput = document.querySelector('#SearchAutocomplete');
            const autocompleteContainer = document.querySelector('#nosto-autocomplete-container');
            
            if (!autocompleteContainer || autocompleteContainer.style.display === 'none') {
                return;
            }

            const focusableElements = autocompleteContainer.querySelectorAll(
                'a[href]:not([tabindex="-1"]), [tabindex]:not([tabindex="-1"]), [role="option"]:not([tabindex="-1"])'
            );

            if (e.key === 'ArrowDown' && document.activeElement === searchInput) {
                e.preventDefault();
                if (focusableElements.length > 0) {
                    focusableElements[0].focus();
                }
            } else if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                e.preventDefault();
                const currentIndex = Array.from(focusableElements).indexOf(document.activeElement);
                let nextIndex;
                
                if (e.key === 'ArrowDown') {
                    nextIndex = currentIndex + 1 >= focusableElements.length ? 0 : currentIndex + 1;
                } else {
                    nextIndex = currentIndex - 1 < 0 ? focusableElements.length - 1 : currentIndex - 1;
                }
                
                if (nextIndex >= 0 && nextIndex < focusableElements.length) {
                    focusableElements[nextIndex].focus();
                }
            } else if (e.key === 'Escape') {
                e.preventDefault();
                searchInput.focus();
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        
        return () => {
            updateSearchAria(null);
            document.removeEventListener('keydown', handleKeyDown);
        }
    }, [products?.hits?.length, keywords?.hits?.length]);

    return (
        <div
            class="ns-autocomplete ns-absolute ns-d-flex ns-flex-column ns-flex-sm-row ns-align-items-start ns-background-white ns-border-box ns-mx-auto"
            data-nosto-element="autocomplete"
            role="listbox"
            aria-label="Search suggestions"
            id="nosto-autocomplete-container"
        >
            <div class="ns-w-100 ns-mt-sm-auto">
                <div class="ns-d-flex ns-flex-column">
                    {trendingCollections && (
                        <div
                            class={`ns-autocomplete-keywords ns-d-flex ns-flex-column ns-col-12`}
                        >
                            <h2 
                                id="trending-collections-heading"
                                class="ns-color-black ns-font-medium ns-pb-3 ns-pb-md-2 ns-autocomplete-heading" 
                                tabIndex="0"
                            >
                                TRENDING COLLECTIONS
                            </h2>
                            <div class="ns-d-flex ns-flex-column" role="group" aria-labelledby="trending-collections-heading">
                                {(window.nostoTrendingCollections || trendingCollections).map((item, index) => (
                                    <a 
                                        class="ns-autocomplete-element ns-d-flex flex-row ns-justify-content-between ns-clickable ns-py-3 ns-py-md-2 ns-font-5" 
                                        href={item?.url}
                                        role="option"
                                        aria-label={`Trending collection: ${item?.name}`}
                                        tabIndex="0"
                                    >
                                        {item?.name}
                                    </a>
                                ))}
                            </div>
                        </div>
                    )}
                    {products?.hits?.length > 0 && (
                        <div class="ns-autocomplete-products ns-d-flex ns-flex-column ns-flex-grow-1">
                            <h2 
                                id="related-products-heading"
                                class="ns-color-black ns-font-medium ns-autocomplete-heading" 
                                tabIndex="0"
                            >
                                RELATED PRODUCTS
                            </h2>
                            <div class="ns-d-flex ns-flex-row" role="group" aria-labelledby="related-products-heading">
                                {products?.hits?.map((hit, index) => {
                                    return (
                                        <AutocompleteElement key={hit.productId} hit={hit}>
                                            <div
                                                data-url={hit.url}
                                                class="ns-d-flex ns-flex-column ns-clickable"
                                                data-nosto-element="product"
                                                tabIndex="0"
                                                role="option"
                                                aria-label={`Product: ${hit.name}`}
                                            >
                                                <img
                                                    class="ns-h-auto ns-object-contain"
                                                    src={hit.imageUrl ?? productImagePlaceholder}
                                                    alt={`Image of ${hit.name}`}
                                                    width="60"
                                                    height="40"
                                                />
                                                <div>
                                                    <h3 class="ns-clipped ns-text-one-line ns-py-1 ns-autocomplete-element-name">
                                                        {hit.name}
                                                    </h3>
                                                </div>
                                            </div>
                                        </AutocompleteElement>
                                    )
                                })}
                            </div>
                        </div>
                    )}
                    {trendingSearches && <div class="ns-top-categories">
                        <h2 class="ns-color-black ns-font-medium ns-pb-3 ns-pb-md-2 ns-autocomplete-heading" tabIndex="0">
                            TRENDING SEARCHES
                        </h2>
                        <div class="ns-d-flex ns-flex-column">
                            {(window.nostoTrendingSearches || trendingSearches).map((item, index)=>{
                                return (
                                    <a 
                                        href={item?.url} 
                                        class="ns-top-category ns-d-block ns-py-3 ns-py-md-2 ns-font-5"
                                        tabIndex="0"
                                        aria-label={`Trending search: ${item?.name}`}
                                    >
                                        {item?.name}
                                    </a>
                                )
                            })}        
                        </div>
                    </div>}
                </div>
            </div>
        </div>
    )
}

function Keyword({ hit }) {
    return (
        <span data-nosto-element="keyword">
            {hit?._highlight?.keyword ? (
                <span
                    dangerouslySetInnerHTML={{
                        __html: hit._highlight.keyword
                    }}
                ></span>
            ) : (
                hit.keyword
            )}
        </span>
    )
}

function Element({ hit, keywordComponent, filter }) {
    return (
        <AutocompleteElement hit={hit} filter={filter} class="ns-clickable ns-py-3 ns-py-md-2 ns-font-5">
            {keywordComponent}
        </AutocompleteElement>
    )
}

function KeywordInCategory({ category, children }) {
    return (
        <span class="ns-clickable">
            {category?.value ? (
                <span>
                    {children}{" "}
                    <span class="ns-keyword-in-category">
                        in <span>{category.value}</span>
                    </span>
                </span>
            ) : (
                children
            )}
        </span>
    )
}


// version removed on October 2nd, 2025, to adapt for ADA compliance

// import { useEffect } from "preact/hooks"
// import { useResponse, AutocompleteElement, Keywords, useCurrencyFormatting, useAppStateSelector, pick } from "@nosto/preact"
// import { productImagePlaceholder, trendingSearches, trendingCollections, updateSearchAria } from "../helpers"
// import SubmitButton from "./elements/SubmitButton"

// export default function Autocomplete() {
//     const { products, keywords } = useResponse()
//     const { format } = useCurrencyFormatting()

//     if (!products?.hits?.length && !keywords?.hits?.length) {
//         return
//     }

//     const { query } = useAppStateSelector(state =>
//                 pick(state, "query")
//     )

//     // Update Aria
//     useEffect(() => {
//         updateSearchAria('autocomplete');
//         return () => updateSearchAria(null); 
//     }, []);

//     return (
//         <div
//             class="ns-autocomplete ns-absolute ns-d-flex ns-flex-column ns-flex-sm-row ns-align-items-start ns-background-white ns-border-box ns-mx-auto"
//             data-nosto-element="autocomplete"
//             role="region"
//             aria-live="polite"
//             id="nosto-autocomplete-container"
//         >
//             <div class="ns-w-100 ns-mt-sm-auto">
//                 <div class="ns-d-flex ns-flex-column">
//                     {trendingCollections && (
//                         <div
//                             class={`ns-autocomplete-keywords ns-d-flex ns-flex-column ns-col-12`}
//                         >
//                             <div class="ns-color-black ns-font-medium ns-pb-3 ns-pb-md-2 ns-autocomplete-heading" 
//                                 tabIndex="0"
//                                 role="heading"
//                                 aria-level="2"
//                             >
//                                 TRENDING COLLECTIONS
//                             </div>
//                             <div class="ns-d-flex ns-flex-column">
//                                 {(window.nostoTrendingCollections || trendingCollections).map(item => (
//                                     <a class="ns-autocomplete-element ns-d-flex flex-row ns-justify-content-between ns-clickable ns-py-3 ns-py-md-2 ns-font-5" href={item?.url}>
//                                         {item?.name}
//                                     </a>
//                                 ))}
//                             </div>
//                         </div>
//                     )}
//                     {products?.hits?.length > 0 && (
//                         <div class="ns-autocomplete-products ns-d-flex ns-flex-column ns-flex-grow-1">
//                             <div class="ns-color-black ns-font-medium ns-autocomplete-heading" tabIndex="0" role="heading"
//                                 aria-level="2">
//                                 RELATED PRODUCTS
//                             </div>
//                             <div class="ns-d-flex ns-flex-row">
//                                 <span role="status" class="sr-only" aria-live="polite">{`Results updated. ${products?.hits?.length} results for "${query?.query}". `}</span>
//                                 {products?.hits?.map(hit => {
//                                     return (
//                                         <AutocompleteElement key={hit.productId} hit={hit}>
//                                             {/* <p class="sr-only" aria-live="polite">{hit?.name}</p> */}
//                                             <div
//                                                 data-url={hit.url}
//                                                 class="ns-d-flex ns-flex-column ns-clickable"
//                                                 data-nosto-element="product"
//                                                 tabIndex="0"
//                                                 role="link"
//                                             >
//                                                 <img
//                                                     class="ns-h-auto ns-object-contain"
//                                                     src={hit.imageUrl ?? productImagePlaceholder}
//                                                     alt={`Image of ${hit.name}`}
//                                                     width="60"
//                                                     height="40"
//                                                 />
//                                                 <div>
//                                                     <div class="ns-clipped ns-text-one-line ns-py-1 ns-autocomplete-element-name">
//                                                         {hit.name}
//                                                     </div>
//                                                 </div>
//                                             </div>
//                                         </AutocompleteElement>
//                                     )
//                                 })}
//                             </div>
//                         </div>
//                     )}
//                     {trendingSearches && <div class="ns-top-categories">
//                         <div class="ns-color-black ns-font-medium ns-pb-3 ns-pb-md-2 ns-autocomplete-heading" tabIndex="0"
//                         role="heading"
//                         aria-level="2">
//                             TRENDING SEARCHES
//                         </div>
//                         <div class="ns-d-flex ns-flex-column">
//                             {(window.nostoTrendingSearches || trendingSearches).map((item, index)=>{
//                                 return (
//                                     <a href={item?.url} class="ns-top-category ns-d-block ns-py-3 ns-py-md-2 ns-font-5">{item?.name}</a>
//                                 )
//                             })}        
//                         </div>
//                     </div>}
//                 </div>
//             </div>
//         </div>
//     )
// }

// function Keyword({ hit }) {
//     return (
//         <span data-nosto-element="keyword">
//             {hit?._highlight?.keyword ? (
//                 <span
//                     dangerouslySetInnerHTML={{
//                         __html: hit._highlight.keyword
//                     }}
//                 ></span>
//             ) : (
//                 hit.keyword
//             )}
//         </span>
//     )
// }

// function Element({ hit, keywordComponent, filter }) {
//     return (
//         <AutocompleteElement hit={hit} filter={filter} class="ns-clickable ns-py-3 ns-py-md-2 ns-font-5">
//             {keywordComponent}
//         </AutocompleteElement>
//     )
// }

// function KeywordInCategory({ category, children }) {
//     return (
//         <span class="ns-clickable">
//             {category?.value ? (
//                 <span>
//                     {children}{" "}
//                     <span class="ns-keyword-in-category">
//                         in <span>{category.value}</span>
//                     </span>
//                 </span>
//             ) : (
//                 children
//             )}
//         </span>
//     )
// }

// original version

// import { useResponse, AutocompleteElement, Keywords, useCurrencyFormatting } from "@nosto/preact"
// import { productImagePlaceholder, trendingSearches, trendingCollections } from "../helpers"
// import SubmitButton from "./elements/SubmitButton"

// export default function Autocomplete() {
//     const { products, keywords } = useResponse()
//     const { format } = useCurrencyFormatting()

//     if (!products?.hits?.length && !keywords?.hits?.length) {
//         return
//     }

//     return (
//         <div
//             class="ns-autocomplete ns-absolute ns-d-flex ns-flex-column ns-flex-sm-row ns-align-items-start ns-background-white ns-border-box ns-mx-auto"
//             data-nosto-element="autocomplete"
//         >
//             <div class="ns-w-100 ns-mt-sm-auto">
//                 <div class="ns-d-flex ns-flex-column">
//                     {trendingCollections && (
//                         <div
//                             class={`ns-autocomplete-keywords ns-d-flex ns-flex-column ns-col-12`}
//                         >
//                             <div class="ns-color-black ns-font-medium ns-pb-3 ns-pb-md-2 ns-autocomplete-heading">
//                                 TRENDING COLLECTIONS
//                             </div>
//                             <div class="ns-d-flex ns-flex-column">
//                                 {(window.nostoTrendingCollections || trendingCollections).map(item => (
//                                     <div class="ns-autocomplete-element ns-d-flex flex-row ns-justify-content-between ns-clickable ns-py-3 ns-py-md-2 ns-font-5" onClick={() => {window.location.href = item?.url}}>
//                                         {item?.name}
//                                     </div>
//                                 ))}
//                             </div>
//                         </div>
//                     )}
//                     {products?.hits?.length > 0 && (
//                         <div class="ns-autocomplete-products ns-d-flex ns-flex-column ns-flex-grow-1">
//                             <div class="ns-color-black ns-font-medium ns-autocomplete-heading">
//                                 RELATED PRODUCTS
//                             </div>
//                             <div class="ns-d-flex ns-flex-row">
//                                 {products?.hits?.map(hit => {
//                                     return (
//                                         <AutocompleteElement key={hit.productId} hit={hit}>
//                                             <div
//                                                 data-url={hit.url}
//                                                 class="ns-d-flex ns-flex-column ns-clickable"
//                                                 data-nosto-element="product"
//                                             >
//                                                 <img
//                                                     class="ns-h-auto ns-object-contain"
//                                                     src={hit.imageUrl ?? productImagePlaceholder}
//                                                     alt={hit.name}
//                                                     width="60"
//                                                     height="40"
//                                                 />
//                                                 <div>
//                                                     <div class="ns-clipped ns-text-one-line ns-py-1 ns-autocomplete-element-name">
//                                                         {hit.name}
//                                                     </div>
//                                                 </div>
//                                             </div>
//                                         </AutocompleteElement>
//                                     )
//                                 })}
//                             </div>
//                         </div>
//                     )}
//                     {trendingSearches && <div class="ns-top-categories">
//                         <div class="ns-color-black ns-font-medium ns-pb-3 ns-pb-md-2 ns-autocomplete-heading">
//                             TRENDING SEARCHES
//                         </div>
//                         <div class="ns-d-flex ns-flex-column">
//                             {(window.nostoTrendingSearches || trendingSearches).map((item, index)=>{
//                                 return (
//                                     <a href={item?.url} class="ns-top-category ns-d-block ns-py-3 ns-py-md-2 ns-font-5">{item?.name}</a>
//                                 )
//                             })}        
//                         </div>
//                     </div>}
//                 </div>
//             </div>
//         </div>
//     )
// }

// function Keyword({ hit }) {
//     return (
//         <span data-nosto-element="keyword">
//             {hit?._highlight?.keyword ? (
//                 <span
//                     dangerouslySetInnerHTML={{
//                         __html: hit._highlight.keyword
//                     }}
//                 ></span>
//             ) : (
//                 hit.keyword
//             )}
//         </span>
//     )
// }

// function Element({ hit, keywordComponent, filter }) {
//     return (
//         <AutocompleteElement hit={hit} filter={filter} class="ns-clickable ns-py-3 ns-py-md-2 ns-font-5">
//             {keywordComponent}
//         </AutocompleteElement>
//     )
// }

// function KeywordInCategory({ category, children }) {
//     return (
//         <span class="ns-clickable">
//             {category?.value ? (
//                 <span>
//                     {children}{" "}
//                     <span class="ns-keyword-in-category">
//                         in <span>{category.value}</span>
//                     </span>
//                 </span>
//             ) : (
//                 children
//             )}
//         </span>
//     )
// }

// first ADA version

// import { useResponse, AutocompleteElement, Keywords, useCurrencyFormatting, useAppStateSelector, pick } from "@nosto/preact";
// import { productImagePlaceholder, trendingSearches, trendingCollections } from "../helpers";
// import SubmitButton from "./elements/SubmitButton";
// import { useEffect, useRef, useState } from "preact/hooks";

// export default function Autocomplete() {
//     const { products, keywords } = useResponse();
//     const { format } = useCurrencyFormatting();
//     const autocompleteRef = useRef(null);
//     const [focusedIndex, setFocusedIndex] = useState(-1);
//     const { query } = useAppStateSelector(state =>
//         pick(state, "query")
//     )

//     useEffect(() => {
//         if (focusedIndex >= 0) {
//             const items = autocompleteRef.current.querySelectorAll('[role="link"], [role="heading"]');
//             if (items[focusedIndex]) {
//                 items[focusedIndex].focus();
//             }
//         }
//     }, [focusedIndex]);

//     const getTotalItems = () => {
//         let totalItems = 0;
//         if (trendingCollections) {
//             totalItems += (window.nostoTrendingCollections || trendingCollections).length + 1; // +1 for the heading
//         }

//         if (trendingSearches) {
//             totalItems += (window.nostoTrendingSearches || trendingSearches).length + 1; // +1 for the heading
//         }

//         const autocompleteItems = document.querySelectorAll('.ns-autocomplete-products [role="link"]');
//         totalItems += autocompleteItems.length + 1; // +1 for the heading

//         return totalItems;
//     };

//     useEffect(() => {
//         const handleKeyDown = (event) => {
//             if (event.key === 'Escape') {
//                 // Hide the autocomplete dropdown if the Escape key is pressed
//                 // You can add your logic to hide the dropdown here
//             } else if (event.key === 'ArrowDown') {
//                 event.preventDefault();
//                 setFocusedIndex((prevIndex) => (prevIndex < getTotalItems() - 1 ? prevIndex + 1 : prevIndex));
//             } else if (event.key === 'ArrowUp') {
//                 event.preventDefault();
//                 setFocusedIndex((prevIndex) => (prevIndex > 0 ? prevIndex - 1 : prevIndex));
//             }
//         };

//         document.addEventListener('keydown', handleKeyDown);
//         return () => {
//             document.removeEventListener('keydown', handleKeyDown);
//         };
//     }, [focusedIndex]);

//     if (!products?.hits?.length && !keywords?.hits?.length) {
//         return null;
//     }

//     return (
//         <div
//             ref={autocompleteRef}
//             className="ns-autocomplete ns-absolute ns-d-flex ns-flex-column ns-flex-sm-row ns-align-items-start ns-background-white ns-border-box ns-mx-auto"
//             aria-live="polite"
//             role="region"
//             aria-labelledby="searchPreviewTitle"
//             id="nosto-autocomplete-container"
//             aria-activedescendant={focusedIndex >= 0 ? `item-${focusedIndex}` : undefined}
//         >
//             <p id="searchResultsDescription" class="sr-only" aria-live="assertive">{`${products?.hits?.length} results matching "${query?.query}".`}</p>
//             <div className="ns-w-100 ns-mt-sm-auto">
//                 <div className="ns-d-flex ns-flex-column">
//                     {trendingCollections && (
//                         <div
//                             className="ns-autocomplete-keywords ns-d-flex ns-flex-column ns-col-12"
//                             aria-labelledby="trending-collections-heading"
//                         >
//                             <div
//                                 id="trending-collections-heading"
//                                 className="ns-color-black ns-font-medium ns-pb-3 ns-pb-md-2 ns-autocomplete-heading"
//                                 tabIndex="0"
//                                 role="heading"
//                                 aria-level="2"
//                             >
//                                 TRENDING COLLECTIONS
//                             </div>
//                             <div className="ns-d-flex ns-flex-column" aria-describedby="trending-collections-heading">
//                                 {(window.nostoTrendingCollections || trendingCollections).map((item, index) => (
//                                     <div
//                                         key={index}
//                                         id={`item-${index}`}
//                                         className="ns-autocomplete-element ns-d-flex flex-row ns-justify-content-between ns-clickable ns-py-3 ns-py-md-2 ns-font-5"
//                                         onClick={() => { window.location.href = item?.url }}
//                                         onKeyPress={(e) => { if (e.key === 'Enter') window.location.href = item?.url }}
//                                         tabIndex="0"
//                                         role="link"
//                                         aria-label={`Trending Collection: ${item?.name}`}
//                                     >
//                                         {item?.name}
//                                     </div>
//                                 ))}
//                             </div>
//                         </div>
//                     )}
//                     {products?.hits?.length > 0 && (
//                         <div className="ns-autocomplete-products ns-d-flex ns-flex-column ns-flex-grow-1" aria-labelledby="related-products-heading">
//                             <div
//                                 id="related-products-heading"
//                                 className="ns-color-black ns-font-medium ns-autocomplete-heading"
//                                 tabIndex="0"
//                                 role="heading"
//                                 aria-level="2"
//                             >
//                                 RELATED PRODUCTS
//                             </div>
//                             <div className="ns-d-flex ns-flex-row">
//                                 {products?.hits?.map((hit, index) => (
//                                     <AutocompleteElement key={hit.productId} hit={hit}>
//                                         <div
//                                             id={`item-${index + (window.nostoTrendingCollections || trendingCollections).length + 1}`}
//                                             data-url={hit.url}
//                                             className="ns-d-flex ns-flex-column ns-clickable"
//                                             data-nosto-element="product"
//                                             tabIndex="0"
//                                             role="link"
//                                             onClick={() => { window.location.href = hit.url }}
//                                             onKeyPress={(e) => { if (e.key === 'Enter') window.location.href = hit.url }}
//                                             aria-label={`Product: ${hit.name}, Price: ${format(hit.price)}`}
//                                         >
//                                             <img
//                                                 className="ns-h-auto ns-object-contain"
//                                                 src={hit.imageUrl ?? productImagePlaceholder}
//                                                 alt={hit.name}
//                                                 width="60"
//                                                 height="40"
//                                             />
//                                             <div>
//                                                 <div className="ns-clipped ns-text-one-line ns-py-1 ns-autocomplete-element-name">
//                                                     {hit.name}
//                                                 </div>
//                                             </div>
//                                         </div>
//                                     </AutocompleteElement>
//                                 ))}
//                             </div>
//                         </div>
//                     )}
//                     {trendingSearches && (
//                         <div className="ns-top-categories">
//                             <div
//                                 id="searchTrendingTitle"
//                                 className="ns-color-black ns-font-medium ns-pb-3 ns-pb-md-2 ns-autocomplete-heading"
//                                 tabIndex="0"
//                                 role="heading"
//                                 aria-level="2"
//                             >
//                                 TRENDING SEARCHES
//                             </div>
//                             <div className="ns-d-flex ns-flex-column" aria-describedby="searchTrendingTitle">
//                                 {(window.nostoTrendingSearches || trendingSearches).map((item, index) => (
//                                     <a
//                                         key={index}
//                                         id={`item-${index + (window.nostoTrendingCollections || trendingCollections).length + products?.hits?.length + 2}`}
//                                         href={item?.url}
//                                         className="ns-top-category ns-d-block ns-py-3 ns-py-md-2 ns-font-5"
//                                         aria-label={`Trending Search: ${item?.name}`}
//                                         tabIndex="0"
//                                         role="link"
//                                         onClick={() => { window.location.href = item?.url }}
//                                         onKeyPress={(e) => { if (e.key === 'Enter') window.location.href = item?.url }}
//                                     >
//                                         {item?.name}
//                                     </a>
//                                 ))}
//                             </div>
//                         </div>
//                     )}
//                 </div>
//             </div>
//         </div>
//     );
// }

// function Keyword({ hit }) {
//     return (
//         <span data-nosto-element="keyword">
//             {hit?._highlight?.keyword ? (
//                 <span
//                     dangerouslySetInnerHTML={{
//                         __html: hit._highlight.keyword
//                     }}
//                 ></span>
//             ) : (
//                 hit.keyword
//             )}
//         </span>
//     );
// }

// function Element({ hit, keywordComponent, filter }) {
//     return (
//         <AutocompleteElement hit={hit} filter={filter} className="ns-clickable ns-py-3 ns-py-md-2 ns-font-5" tabIndex="0" role="link">
//             {keywordComponent}
//         </AutocompleteElement>
//     );
// }

// function KeywordInCategory({ category, children }) {
//     return (
//         <span className="ns-clickable">
//             {category?.value ? (
//                 <span>
//                     {children}{" "}
//                     <span className="ns-keyword-in-category">
//                         in <span>{category.value}</span>
//                     </span>
//                 </span>
//             ) : (
//                 children
//             )}
//         </span>
//     );
// }
